/**
 * Storage layer for selection-history snapshots.
 *
 * Serialises the nested Map<chunkIndex, Map<selectionId, RangeSelection>>
 * structure to/from a JSON payload in localStorage. Split out of
 * SelectionHistoryContext.tsx so the pure data transforms (clone,
 * serialise, parse, save, load) can be unit-tested without spinning up
 * a React provider.
 *
 * The load path carries backwards-compatibility for two older formats:
 *   1. A wrapper object with `current`, `undoStack`, `redoStack` keys
 *      (undo/redo was removed in favour of a show/hide toggle).
 *   2. A direct JSON snapshot at the top level (the oldest format).
 * Both rehydrate into the current shape. Every test on this module must
 * lock those paths in, because a silent regression would lose users'
 * selection history on upgrade and we'd only hear about it from
 * complaints.
 */

import type { RangeSelection } from './types';

/**
 * Nested map: chunkIndex → (selectionId → RangeSelection). One snapshot
 * represents the full selection state of a single article at one point
 * in time.
 */
export type ArticleSnapshot = Map<number, Map<string, RangeSelection>>;

export const STORAGE_PREFIX = 'polyreader_selections_';

/**
 * Deep clone: every nested Map must be a fresh instance so mutating
 * the clone's inner Map doesn't leak back into the original. The
 * RangeSelection objects themselves are treated as immutable (the
 * context always creates new ones via spread), so they're shared by
 * reference — that's a deliberate optimisation, not a bug.
 */
export function cloneSnapshot(snap: ArticleSnapshot): ArticleSnapshot {
  const clone = new Map<number, Map<string, RangeSelection>>();
  for (const [k, v] of snap) {
    clone.set(k, new Map(v));
  }
  return clone;
}

export function snapshotToJson(snap: ArticleSnapshot): string {
  const obj: Record<string, Record<string, RangeSelection>> = {};
  for (const [chunkIndex, sels] of snap) {
    obj[chunkIndex] = Object.fromEntries(sels);
  }
  return JSON.stringify(obj);
}

export function jsonToSnapshot(json: string): ArticleSnapshot {
  const obj = JSON.parse(json) as Record<string, Record<string, RangeSelection>>;
  const snap: ArticleSnapshot = new Map();
  for (const [key, sels] of Object.entries(obj)) {
    snap.set(Number(key), new Map(Object.entries(sels)));
  }
  return snap;
}

/**
 * Persist the current snapshot to localStorage under the article-keyed
 * prefix. Quota-exceeded and unavailable-storage errors are swallowed
 * silently — the same behaviour the inline version shipped with; losing
 * a save is less disruptive than losing an entire render pass.
 */
export function saveToStorage(
  articleId: string,
  current: ArticleSnapshot,
): void {
  try {
    const data = JSON.stringify({
      current: snapshotToJson(current),
    });
    localStorage.setItem(STORAGE_PREFIX + articleId, data);
  } catch {
    /* storage full or unavailable */
  }
}

/**
 * Rehydrate a stored snapshot, handling three formats:
 *   1. New format: `{ current }` (current code).
 *   2. Undo/redo format: `{ current, undoStack, redoStack }` — stacks
 *      are silently discarded.
 *   3. Legacy format: top-level JSON snapshot, no wrapper object.
 *
 * Returns null if the key is missing or the payload is malformed.
 * Throws only in the most pathological cases; callers shouldn't
 * need to try/catch the return.
 */
export function loadFromStorage(articleId: string): ArticleSnapshot | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + articleId);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // New format (and undo/redo format): `current` is always a string
    // here (the inner JSON from snapshotToJson) so this check
    // disambiguates from the legacy top-level-snapshot case where
    // `data` is a plain Record<string, Record<...>>.
    if (data.current) {
      return jsonToSnapshot(data.current);
    }
    // Old format: the whole localStorage value is the snapshot JSON
    // itself. Re-parse `raw` (not `data`) via jsonToSnapshot.
    return jsonToSnapshot(raw);
  } catch {
    return null;
  }
}
